import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { policyAcksQuery, remindPolicyAcks, sendPolicyAcks, type PolicyAcksPage } from '../api/easeedeskPolicyAcks'
import { toast } from '../ui/notify'

/**
 * IT Policies > Confirmations (CyFun GV.PO-01.1): trimite textul NIS2 al
 * politicii angajatilor activi ai firmei din antet, fiecare cu link personal,
 * si arata cine a confirmat. Trimiterea si reamintirea pleaca la clienti, deci
 * cer o a doua apasare.
 */
/* Tabelul sta in randul desfasurat al listei DataTables, a carei foaie face capetele sticky si centrate. */
const HEAD = { position: 'static', textAlign: 'left' } as const

export function PolicyAcksPanel({ subcategory }: { subcategory: number }) {
  const client = useQueryClient()
  const q = useQuery(policyAcksQuery(subcategory))
  const [asking, setAsking] = useState<'send' | 'remind' | null>(null)
  const [busy, setBusy] = useState(false)
  const data = q.data

  if (q.isError) return <div className="alert alert-danger">{q.error.message}</div>
  if (!data) return null
  if (!data.ready) return <div className="alert alert-warning">Policy confirmations are not installed yet. They come with the next database migration.</div>
  if (data.company === null) return <div className="alert alert-info">Pick one company in the header: the policy is sent to the employees of one company.</div>

  const round = data.round
  const pending = data.people.filter((p) => p.confirmed_at === null).length

  const run = async (action: 'send' | 'remind') => {
    setBusy(true)
    try {
      const next: PolicyAcksPage = action === 'send' ? await sendPolicyAcks(subcategory) : await remindPolicyAcks(round?.id ?? 0)
      client.setQueryData(policyAcksQuery(subcategory).queryKey, next)
      const done = action === 'send' ? next.sent ?? 0 : next.reminded ?? 0
      toast(`${done} email${done === 1 ? '' : 's'} sent${next.failed ? `, ${next.failed} failed` : ''}.`, 'Policy confirmation', next.failed ? 'warning' : 'success')
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Not sent', 'Error', 'error')
    } finally {
      setBusy(false)
      setAsking(null)
    }
  }

  return (
    <div className="policy-acks">
      <p className="text-muted" style={{ fontSize: 12 }}>
        Sends the NIS2 text of this policy to the active employees of <strong>{data.company.name}</strong> who have an email address. Each gets a personal
        link and confirms without an account; the date is kept as evidence (CyFun GV.PO-01.1).
      </p>
      {round ? (
        <p className="policy-acks-round">
          Last sent {round.sent_at.slice(0, 16)}: <strong>{round.confirmed} of {round.recipients}</strong> confirmed.
        </p>
      ) : (
        <p className="policy-acks-round">Not sent yet. It would go to {data.recipients} employee{data.recipients === 1 ? '' : 's'}.</p>
      )}
      {data.canSend && (
        <div className="policy-acks-actions" style={{ marginBottom: 12 }}>
          {asking ? (
            <span role="group" aria-label="Confirm sending">
              {asking === 'send' ? `Email the policy to ${data.recipients} employee${data.recipients === 1 ? '' : 's'}?` : `Email a new link to the ${pending} who have not confirmed?`}{' '}
              <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => void run(asking)}>
                {busy ? 'Sending...' : 'Yes, send'}
              </button>{' '}
              <button type="button" className="btn btn-default btn-sm" disabled={busy} onClick={() => setAsking(null)}>
                Cancel
              </button>
            </span>
          ) : (
            <>
              <button type="button" className="btn btn-primary btn-sm" disabled={data.recipients < 1} onClick={() => setAsking('send')}>
                <i className="fa fa-paper-plane" aria-hidden="true"></i> {round ? 'Send again (new round)' : 'Send for confirmation'}
              </button>{' '}
              {round && pending > 0 && (
                <button type="button" className="btn btn-default btn-sm" onClick={() => setAsking('remind')}>
                  <i className="fa fa-bell" aria-hidden="true"></i> Remind {pending}
                </button>
              )}
            </>
          )}
        </div>
      )}
      {data.people.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table className="table table-condensed policy-acks-people">
            <thead>
              <tr>
                {['Employee', 'Email', 'Sent', 'Confirmed'].map((h) => (
                  <th key={h} style={HEAD}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.people.map((p) => (
                <tr key={p.id}>
                  <td>{p.name}</td>
                  <td>{p.email}</td>
                  <td>{(p.reminded_at ?? p.sent_at).slice(0, 16)}{p.reminded_at ? ' (reminded)' : ''}</td>
                  <td>{p.confirmed_at ? p.confirmed_at.slice(0, 16) : <span className="text-muted">not yet</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
